// FirestoreService: Handles all Firestore operations for revision systems
// Configurable to support both Greek and General revision systems
import {
  collection,
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  updateDoc,
  getDocs,
  addDoc,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import { firestore } from '../../firebase-config';
import {
  Card,
  CardInput,
  Section,
  ReviewRecord,
  LegacySection,
  LegacyCard,
  ScheduleData,
  Rating,
  FormattedText
} from '../types/models';
import { RevisionSystemConfig } from '../config/RevisionSystemConfig';

/**
 * Service class for Firestore operations
 * Handles CRUD operations for cards, sections, and review records
 * Configurable to support different revision systems (Greek, General, etc.)
 */
export class FirestoreService {
  private userId: string | null = null;
  private config: RevisionSystemConfig;

  /**
   * Creates a new FirestoreService instance
   * @param config - Configuration for the revision system
   */
  constructor(config: RevisionSystemConfig) {
    this.config = config;
  }

  /**
   * Set the user ID for user-scoped operations
   * @param userId - The user's UID
   */
  setUserId(userId: string): void {
    this.userId = userId;
  }

  /**
   * Get the collection path for user-scoped data
   * @param collectionType - The type of collection (sections, cards, or reviews)
   * @returns The full collection path
   */
  private getCollectionPath(collectionType: 'sections' | 'cards' | 'reviews'): string {
    if (!this.userId) {
      throw new Error('User ID not set. Call setUserId() before performing operations.');
    }
    const collectionName = this.config.collections[collectionType];
    return `users/${this.userId}/${collectionName}`;
  }

  /**
   * Creates a new card in Firestore
   * @param cardInput - The card data to create
   * @returns Promise resolving to the created card with generated ID
   * @throws Error if creation fails
   */
  async createCard(cardInput: CardInput): Promise<Card> {
    try {
      const now = new Date();
      
      // Initialize schedule data for new card
      const initialSchedule: ScheduleData = {
        nextReviewDate: now, // Available for review immediately
        interval: 0,
        easeFactor: 2.5, // Default SM-2 ease factor
        repetitions: 0,
        lastReviewDate: null
      };

      // Create card document reference
      const cardsRef = collection(firestore, this.getCollectionPath('cards'));
      const docRef = await addDoc(cardsRef, {
        sectionId: cardInput.sectionId,
        front: cardInput.front,
        back: cardInput.back,
        tags: cardInput.tags || [],
        schedule: {
          nextReviewDate: now.toISOString(),
          interval: initialSchedule.interval,
          easeFactor: initialSchedule.easeFactor,
          repetitions: initialSchedule.repetitions,
          lastReviewDate: null
        },
        createdAt: now.toISOString(),
        updatedAt: now.toISOString()
      });

      // Update section cardCount
      const sectionRef = doc(firestore, this.getCollectionPath('sections'), cardInput.sectionId);
      const sectionSnap = await getDoc(sectionRef);
      if (sectionSnap.exists()) {
        const currentCount = sectionSnap.data().cardCount || 0;
        await updateDoc(sectionRef, {
          cardCount: currentCount + 1,
          updatedAt: now.toISOString()
        });
      }

      // Return the created card with ID
      const card: Card = {
        id: docRef.id,
        sectionId: cardInput.sectionId,
        front: cardInput.front,
        back: cardInput.back,
        tags: cardInput.tags || [],
        schedule: initialSchedule,
        createdAt: now,
        updatedAt: now
      };

      return card;
    } catch (error) {
      console.error('Error creating card:', error);
      throw new Error(`Failed to create card: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Updates an existing card in Firestore
   * @param cardId - The ID of the card to update
   * @param updates - Partial card data to update
   * @throws Error if update fails
   */
  async updateCard(cardId: string, updates: Partial<Card>): Promise<void> {
    try {
      const cardRef = doc(firestore, this.getCollectionPath('cards'), cardId);
      
      // Prepare update data, converting dates to ISO strings
      const updateData: any = {
        ...updates,
        updatedAt: new Date().toISOString()
      };

      // Convert schedule dates if schedule is being updated
      if (updates.schedule) {
        updateData.schedule = {
          ...updates.schedule,
          nextReviewDate: updates.schedule.nextReviewDate instanceof Date 
            ? updates.schedule.nextReviewDate.toISOString()
            : updates.schedule.nextReviewDate,
          lastReviewDate: updates.schedule.lastReviewDate instanceof Date
            ? updates.schedule.lastReviewDate.toISOString()
            : updates.schedule.lastReviewDate
        };
      }

      // Remove id field if present (can't update document ID)
      delete updateData.id;
      delete updateData.createdAt; // Don't update creation date

      await updateDoc(cardRef, updateData);
    } catch (error) {
      console.error('Error updating card:', error);
      throw new Error(`Failed to update card: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Deletes a card from Firestore
   * @param cardId - The ID of the card to delete
   * @throws Error if deletion fails
   */
  async deleteCard(cardId: string): Promise<void> {
    try {
      // Get the card first to retrieve its sectionId
      const cardRef = doc(firestore, this.getCollectionPath('cards'), cardId);
      const cardSnap = await getDoc(cardRef);
      
      if (!cardSnap.exists()) {
        throw new Error(`Card with ID ${cardId} not found`);
      }
      
      const sectionId = cardSnap.data().sectionId;
      
      // Delete the card
      await deleteDoc(cardRef);
      
      // Update section cardCount
      const sectionRef = doc(firestore, this.getCollectionPath('sections'), sectionId);
      const sectionSnap = await getDoc(sectionRef);
      if (sectionSnap.exists()) {
        const currentCount = sectionSnap.data().cardCount || 0;
        await updateDoc(sectionRef, {
          cardCount: Math.max(0, currentCount - 1), // Ensure count doesn't go negative
          updatedAt: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error deleting card:', error);
      throw new Error(`Failed to delete card: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Retrieves a single card by ID
   * @param cardId - The ID of the card to retrieve
   * @returns Promise resolving to the card
   * @throws Error if card not found or retrieval fails
   */
  async getCard(cardId: string): Promise<Card> {
    try {
      const cardRef = doc(firestore, this.getCollectionPath('cards'), cardId);
      const cardSnap = await getDoc(cardRef);

      if (!cardSnap.exists()) {
        throw new Error(`Card with ID ${cardId} not found`);
      }

      const data = cardSnap.data();
      
      // Transform Firestore document to Card model
      const card: Card = {
        id: cardSnap.id,
        sectionId: data.sectionId,
        front: data.front,
        back: data.back,
        tags: data.tags || [],
        schedule: {
          nextReviewDate: new Date(data.schedule.nextReviewDate),
          interval: data.schedule.interval,
          easeFactor: data.schedule.easeFactor,
          repetitions: data.schedule.repetitions,
          lastReviewDate: data.schedule.lastReviewDate ? new Date(data.schedule.lastReviewDate) : null
        },
        createdAt: new Date(data.createdAt),
        updatedAt: new Date(data.updatedAt)
      };

      return card;
    } catch (error) {
      console.error('Error getting card:', error);
      throw new Error(`Failed to get card: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Retrieves all cards in a specific section
   * @param sectionId - The ID of the section
   * @returns Promise resolving to array of cards
   * @throws Error if retrieval fails
   */
  async getCardsBySection(sectionId: string): Promise<Card[]> {
    try {
      const cardsRef = collection(firestore, this.getCollectionPath('cards'));
      const q = query(cardsRef, where('sectionId', '==', sectionId));
      const querySnapshot = await getDocs(q);

      const cards: Card[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        cards.push({
          id: doc.id,
          sectionId: data.sectionId,
          front: data.front,
          back: data.back,
          tags: data.tags || [],
          schedule: {
            nextReviewDate: new Date(data.schedule.nextReviewDate),
            interval: data.schedule.interval,
            easeFactor: data.schedule.easeFactor,
            repetitions: data.schedule.repetitions,
            lastReviewDate: data.schedule.lastReviewDate ? new Date(data.schedule.lastReviewDate) : null
          },
          createdAt: new Date(data.createdAt),
          updatedAt: new Date(data.updatedAt)
        });
      });

      return cards;
    } catch (error) {
      console.error('Error getting cards by section:', error);
      throw new Error(`Failed to get cards by section: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Creates a new section in Firestore
   * @param name - The name of the section
   * @returns Promise resolving to the created section with generated ID
   * @throws Error if creation fails
   */
  async createSection(name: string): Promise<Section> {
    try {
      const now = new Date();

      // Create section document reference
      const sectionsRef = collection(firestore, this.getCollectionPath('sections'));
      const docRef = await addDoc(sectionsRef, {
        name,
        description: '',
        cardCount: 0,
        createdAt: now.toISOString(),
        updatedAt: now.toISOString()
      });

      // Return the created section with ID
      const section: Section = {
        id: docRef.id,
        name,
        description: '',
        cardCount: 0,
        createdAt: now,
        updatedAt: now
      };

      return section;
    } catch (error) {
      console.error('Error creating section:', error);
      throw new Error(`Failed to create section: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Updates an existing section in Firestore
   * @param sectionId - The ID of the section to update
   * @param updates - Partial section data to update
   * @throws Error if update fails
   */
  async updateSection(sectionId: string, updates: Partial<Section>): Promise<void> {
    try {
      const sectionRef = doc(firestore, this.getCollectionPath('sections'), sectionId);

      // Prepare update data
      const updateData: any = {
        ...updates,
        updatedAt: new Date().toISOString()
      };

      // Remove id field if present (can't update document ID)
      delete updateData.id;
      delete updateData.createdAt; // Don't update creation date

      await updateDoc(sectionRef, updateData);
    } catch (error) {
      console.error('Error updating section:', error);
      throw new Error(`Failed to update section: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Deletes a section from Firestore
   * @param sectionId - The ID of the section to delete
   * @throws Error if deletion fails
   */
  async deleteSection(sectionId: string): Promise<void> {
    try {
      const sectionRef = doc(firestore, this.getCollectionPath('sections'), sectionId);
      await deleteDoc(sectionRef);
    } catch (error) {
      console.error('Error deleting section:', error);
      throw new Error(`Failed to delete section: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Retrieves a single section by ID
   * @param sectionId - The ID of the section to retrieve
   * @returns Promise resolving to the section
   * @throws Error if section not found or retrieval fails
   */
  async getSection(sectionId: string): Promise<Section> {
    try {
      const sectionRef = doc(firestore, this.getCollectionPath('sections'), sectionId);
      const sectionSnap = await getDoc(sectionRef);

      if (!sectionSnap.exists()) {
        throw new Error(`Section with ID ${sectionId} not found`);
      }

      const data = sectionSnap.data();

      // Transform Firestore document to Section model
      const section: Section = {
        id: sectionSnap.id,
        name: data.name,
        description: data.description,
        cardCount: data.cardCount || 0,
        createdAt: new Date(data.createdAt),
        updatedAt: new Date(data.updatedAt)
      };

      return section;
    } catch (error) {
      console.error('Error getting section:', error);
      throw new Error(`Failed to get section: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Retrieves all sections
   * @returns Promise resolving to array of sections
   * @throws Error if retrieval fails
   */
  async getAllSections(): Promise<Section[]> {
    try {
      const sectionsRef = collection(firestore, this.getCollectionPath('sections'));
      const querySnapshot = await getDocs(sectionsRef);

      const sections: Section[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        sections.push({
          id: doc.id,
          name: data.name,
          description: data.description,
          cardCount: data.cardCount || 0,
          createdAt: new Date(data.createdAt),
          updatedAt: new Date(data.updatedAt)
        });
      });

      return sections;
    } catch (error) {
      console.error('Error getting all sections:', error);
      throw new Error(`Failed to get all sections: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Records a review of a card
   * @param review - The review record to save (without ID)
   * @returns Promise resolving when the review is recorded
   * @throws Error if recording fails
   */
  async recordReview(review: Omit<ReviewRecord, 'id'>): Promise<void> {
    try {
      const reviewsRef = collection(firestore, this.getCollectionPath('reviews'));
      await addDoc(reviewsRef, {
        cardId: review.cardId,
        sectionId: review.sectionId,
        rating: review.rating,
        reviewDate: review.reviewDate.toISOString(),
        timeSpent: review.timeSpent,
        previousInterval: review.previousInterval,
        newInterval: review.newInterval
      });
    } catch (error) {
      console.error('Error recording review:', error);
      throw new Error(`Failed to record review: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Retrieves the review history for a specific card
   * @param cardId - The ID of the card
   * @returns Promise resolving to array of review records, ordered by review date (newest first)
   * @throws Error if retrieval fails
   */
  async getReviewHistory(cardId: string): Promise<ReviewRecord[]> {
    try {
      const reviewsRef = collection(firestore, this.getCollectionPath('reviews'));
      const q = query(
        reviewsRef,
        where('cardId', '==', cardId),
        orderBy('reviewDate', 'desc')
      );
      const querySnapshot = await getDocs(q);

      const reviews: ReviewRecord[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        reviews.push({
          id: doc.id,
          cardId: data.cardId,
          sectionId: data.sectionId,
          rating: data.rating as Rating,
          reviewDate: new Date(data.reviewDate),
          timeSpent: data.timeSpent,
          previousInterval: data.previousInterval,
          newInterval: data.newInterval
        });
      });

      return reviews;
    } catch (error) {
      console.error('Error getting review history:', error);
      throw new Error(`Failed to get review history: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Retrieves all cards that are due for review on or before a specific date
   * @param beforeDate - The date to check against (cards due on or before this date)
   * @returns Promise resolving to array of cards due for review
   * @throws Error if retrieval fails
   */
  async getDueCards(beforeDate: Date): Promise<Card[]> {
    try {
      const cardsRef = collection(firestore, this.getCollectionPath('cards'));
      const q = query(
        cardsRef,
        where('schedule.nextReviewDate', '<=', beforeDate.toISOString()),
        orderBy('schedule.nextReviewDate', 'asc')
      );
      const querySnapshot = await getDocs(q);

      const cards: Card[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        cards.push({
          id: doc.id,
          sectionId: data.sectionId,
          front: data.front,
          back: data.back,
          tags: data.tags || [],
          schedule: {
            nextReviewDate: new Date(data.schedule.nextReviewDate),
            interval: data.schedule.interval,
            easeFactor: data.schedule.easeFactor,
            repetitions: data.schedule.repetitions,
            lastReviewDate: data.schedule.lastReviewDate ? new Date(data.schedule.lastReviewDate) : null
          },
          createdAt: new Date(data.createdAt),
          updatedAt: new Date(data.updatedAt)
        });
      });

      return cards;
    } catch (error) {
      console.error('Error getting due cards:', error);
      throw new Error(`Failed to get due cards: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Retrieves all cards in the system
   * @returns Promise resolving to array of all cards
   * @throws Error if retrieval fails
   */
  async getAllCards(): Promise<Card[]> {
    try {
      const cardsRef = collection(firestore, this.getCollectionPath('cards'));
      const querySnapshot = await getDocs(cardsRef);

      const cards: Card[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        cards.push({
          id: doc.id,
          sectionId: data.sectionId,
          front: data.front,
          back: data.back,
          tags: data.tags || [],
          schedule: {
            nextReviewDate: new Date(data.schedule.nextReviewDate),
            interval: data.schedule.interval,
            easeFactor: data.schedule.easeFactor,
            repetitions: data.schedule.repetitions,
            lastReviewDate: data.schedule.lastReviewDate ? new Date(data.schedule.lastReviewDate) : null
          },
          createdAt: new Date(data.createdAt),
          updatedAt: new Date(data.updatedAt)
        });
      });

      return cards;
    } catch (error) {
      console.error('Error getting all cards:', error);
      throw new Error(`Failed to get all cards: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Retrieves all review records in the system
   * @returns Promise resolving to array of all review records
   * @throws Error if retrieval fails
   */
  async getAllReviews(): Promise<ReviewRecord[]> {
    try {
      const reviewsRef = collection(firestore, this.getCollectionPath('reviews'));
      const querySnapshot = await getDocs(reviewsRef);

      const reviews: ReviewRecord[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        reviews.push({
          id: doc.id,
          cardId: data.cardId,
          sectionId: data.sectionId,
          rating: data.rating as Rating,
          reviewDate: new Date(data.reviewDate),
          timeSpent: data.timeSpent,
          previousInterval: data.previousInterval,
          newInterval: data.newInterval
        });
      });

      return reviews;
    } catch (error) {
      console.error('Error getting all reviews:', error);
      throw new Error(`Failed to get all reviews: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Finds all legacy sections for migration based on the configured filter
   * @returns Promise resolving to array of legacy sections with their cards
   * @throws Error if retrieval fails
   */
  async findLegacySections(): Promise<LegacySection[]> {
    try {
      // Query all sections from the legacy collection (GLOBAL, not user-scoped)
      // Legacy sections are stored at the root level: sections/{sectionId}
      const legacySectionsRef = collection(firestore, this.config.migration.legacyCollection);
      const querySnapshot = await getDocs(legacySectionsRef);

      const legacySections: LegacySection[] = [];

      // Process each section
      for (const sectionDoc of querySnapshot.docs) {
        const sectionData = sectionDoc.data();
        const name = sectionData.name || '';

        // Filter sections using the configured filter function
        if (!this.config.migration.sectionNameFilter(name)) {
          continue; // Skip sections that don't pass the filter
        }

        // Get cards for this section
        // Legacy cards are stored as subcollection: sections/{sectionDocId}/notes
        // Use the document ID (not the name) as the path
        const cards: LegacyCard[] = [];

        // Cards are stored in the "notes" subcollection (legacy naming)
        // Use the section document ID as the path
        const cardsRef = collection(firestore, this.config.migration.legacyCollection, sectionDoc.id, 'notes');
        const cardsSnapshot = await getDocs(cardsRef);

        if (!cardsSnapshot.empty) {
          // Cards found in subcollection
          cardsSnapshot.forEach((cardDoc) => {
            const cardData = cardDoc.data();
            // Legacy cards have "title" as front and "content" as back
            const legacyCard: LegacyCard = {
              id: cardDoc.id,
              front: cardData.title || '',
              back: cardData.content || '',
              nextReview: cardData.nextReview,
              interval: cardData.interval,
              easeFactor: cardData.easeFactor,
              repetitions: cardData.repetitions
            };
            cards.push(legacyCard);
          });
        }

        // Add legacy section with its cards
        legacySections.push({
          id: sectionDoc.id,
          title: name,
          cards
        });
      }

      return legacySections;
    } catch (error) {
      console.error('Error finding legacy sections:', error);
      throw new Error(`Failed to find legacy sections: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Migrates a single legacy card to the new card format
   * @param legacyCard - The legacy card to migrate
   * @param sectionId - The ID of the section to migrate the card to
   * @returns Promise resolving to the newly created card
   * @throws Error if migration fails
   */
  async migrateLegacyCard(legacyCard: LegacyCard, sectionId: string): Promise<Card> {
    try {
      // Transform legacy card content to FormattedText format
      // Legacy cards have plain string content, convert to FormattedText
      const front: FormattedText = {
        plainText: legacyCard.front,
        html: legacyCard.front // No formatting in legacy cards, use plain text as HTML
      };

      const back: FormattedText = {
        plainText: legacyCard.back,
        html: legacyCard.back // No formatting in legacy cards, use plain text as HTML
      };

      // Preserve scheduling data if it exists, otherwise use defaults
      const now = new Date();
      const schedule: ScheduleData = {
        nextReviewDate: legacyCard.nextReview
          ? new Date(legacyCard.nextReview)
          : now, // If no nextReview, make available immediately
        interval: legacyCard.interval ?? 0,
        easeFactor: legacyCard.easeFactor ?? 2.5, // Default SM-2 ease factor
        repetitions: legacyCard.repetitions ?? 0,
        lastReviewDate: legacyCard.nextReview ? new Date(legacyCard.nextReview) : null
      };

      // Create the card using the existing createCard method
      // But we need to use a lower-level approach to preserve the schedule data
      const cardsRef = collection(firestore, this.getCollectionPath('cards'));
      const docRef = await addDoc(cardsRef, {
        sectionId: sectionId,
        front: front,
        back: back,
        tags: [], // Legacy cards don't have tags
        schedule: {
          nextReviewDate: schedule.nextReviewDate.toISOString(),
          interval: schedule.interval,
          easeFactor: schedule.easeFactor,
          repetitions: schedule.repetitions,
          lastReviewDate: schedule.lastReviewDate ? schedule.lastReviewDate.toISOString() : null
        },
        createdAt: now.toISOString(),
        updatedAt: now.toISOString()
      });

      // Update section cardCount
      const sectionRef = doc(firestore, this.getCollectionPath('sections'), sectionId);
      const sectionSnap = await getDoc(sectionRef);
      if (sectionSnap.exists()) {
        const currentCount = sectionSnap.data().cardCount || 0;
        await updateDoc(sectionRef, {
          cardCount: currentCount + 1,
          updatedAt: now.toISOString()
        });
      }

      // Return the migrated card
      const card: Card = {
        id: docRef.id,
        sectionId: sectionId,
        front: front,
        back: back,
        tags: [],
        schedule: schedule,
        createdAt: now,
        updatedAt: now
      };

      return card;
    } catch (error) {
      console.error('Error migrating legacy card:', error);
      throw new Error(`Failed to migrate legacy card: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}
